const remunerationCycleAbbreviations = Object.freeze({
    day: 'p/d',
    hour: 'p/h',
    year: 'p/a'
});

/**
 * Formats remuneration values into a readable string with proper currency symbols and cycles.
 *
 * @param {string} currencyCode - The ISO 4217 currency code to format the remuneration values (e.g., USD, EUR).
 * @param {number} remunerationMin - The minimum remuneration value to be formatted.
 * @param {number|null} remunerationMax - The maximum remuneration value to be formatted, or null if unavailable.
 * @param {string} remunerationCycle - The cycle of remuneration (e.g., hourly, monthly, annually).
 * @return {string} - A formatted string representing the remuneration range and cycle.
 */
export function formattedRemuneration(currencyCode, remunerationMin, remunerationMax, remunerationCycle) {
    const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const remunerationCycleAbbr = remunerationCycleAbbreviations[remunerationCycle.toLowerCase()];
    const formattedMin = removeUnnecessaryDecimals(formatter.format(remunerationMin));
    if (remunerationMax === null || remunerationMin === remunerationMax) {
        return `${formattedMin} ${remunerationCycleAbbr}`;
    }
    const formattedMax = removeUnnecessaryDecimals(formatter.format(remunerationMax)).replace(/[^\d.,]/g, '');
    const range = `${formattedMin}-$${formattedMax}`;
    return `${range} ${remunerationCycleAbbr}`;
}

/**
 * Removes unnecessary decimals (e.g., `.00`) from the input value while retaining other valid decimal points.
 *
 * @param {string} value - The input string representing a numeric value that may contain decimals.
 * @return {string} - The formatted string with unnecessary decimals removed.
 */
function removeUnnecessaryDecimals(value) {
    if (value.includes('.') && value.endsWith('00')) {
        return value.replace('.00', '');
    }
    return value;
}
